<template>
  <section>
    <errors v-if="isVisibleErrorsModal"></errors>
    <question v-if="display"></question>
    <information v-if="isInformationModalVisible"></information>
    <bank-account-changed-warning
      :isVisible="isBankAccountChangedWarningVisible"
      @close="hideBankAccountChangedWarning"
      @goToUpcomingTransactions="handleGoToUpcomingTransactions" />
    <div class="content-wrapper">
      <div class="content-heading">
        Manage bank accounts
        <small data-localize="dashboard.WELCOME"></small>
      </div>

      <div class="row">
        <spinner v-if="loading"></spinner>
        <div class="col-lg-12">
          <onboarding-wizard :userStatus="userStatus" v-if="!onBoardingCompleted"></onboarding-wizard>

          <div id="panelChart9" class="panel panel-default panel-height">
            <dismissable-alert
              :title="`You've successfully ${fundingSourceAction} a bank account!`"
              :closeAction="hideFundingSourceActionSuccess"
              :isShowing="showFundingSourceActionSuccess"
            />
            <div v-if="legacyFundingSourcesExist()" class="alert alert-info" role="alert">
              <h4><em class="icon-info pr-sm"></em>{{ legacyFundingSourceMessageFirstLine }}</h4>
              <p>{{ legacyFundingSourceMessageSecondLine }}</p>
            </div>

            <div v-if="showPendingMicrodepositsLabel" class="alert alert-info" role="alert">
              <h4><em class="icon-info pr-sm"></em>Please note you have pending micro-deposits for bank accounts below.</h4>
            </div>

            <div v-if="fundingSources.length === 0" class="alert alert-info mt-lg" role="alert">
              <h4><em class="icon-info pr-sm"></em>Please note that you don't have linked bank accounts at the moment.</h4>
              <p>Securely connect your bank account below to seamlessly invest and receive payments to your account.</p>
            </div>

            <div v-if="fundingSources.length > 0 && !showFundingTable" class="alert alert-info mt-lg" role="alert">
              <p><em class="icon-info pr-sm"></em>Securely connect your bank account below to seamlessly invest and receive payments to your account.</p>
            </div>

            <div v-if="fundingSources.length > 0 && showFundingTable" class="accounts-title top-margin left-margin">
              <div class="panel-title">
                Accounts list
              </div>
            </div>

            <!-- Desktop Table View -->
            <div class="table-responsive top-margin desktop-table" v-show="showFundingTable">
              <table class="table table-striped bank-accounts table-fundings">
                <thead>
                  <tr>
                    <th class="text-center">Bank name</th>
                    <th class="text-center">Account name</th>
                    <th class="text-center">Type</th>
                    <th class="text-center">Default</th>
                    <th class="text-center">Status</th>
                    <th class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(funding, index) in fundingSources" :key="index">
                    <td class="text-center">{{funding.bankName ? funding.bankName : '-----'}}</td>
                    <td class="text-center">{{funding.name}}</td>
                    <td class="text-center">{{formatType(funding.type, 'investor')}}</td>
                    <td class="text-center">
                      <input v-if="funding.status === 'verified'" type="radio" v-bind:value="funding.id"
                        v-model="user.accounts[activeAccountIndex].defaultFundingSourceId"
                        :disabled="isReadOnly"
                        @change="showConfirmationModal({ fundingSource: funding })" />
                    </td>
                    <td class="text-center">
                      <span class="label" v-if="funding.type !== fundingSourceTypes.CHECK.name && funding.type !== fundingSourceTypes.WIRE_TRANSFER.name"
                      v-bind:class="funding.status === 'verified' ? 'label-success' : 'label-warning'">
                        {{funding.status}}
                      </span>
                    </td>
                    <td class="text-center">
                      <a class="text-red" href="#"
                        :style="{ pointerEvents: isReadOnly ? 'none' : null, opacity: isReadOnly ? 0.5 : 1 }"
                        @click.prevent="removeFundingSource(funding.id)">
                        <em class="fa fa-trash" ></em></a>
                        <button v-if="funding.status !== 'verified'" type="button"
                          class="btn btn-details btn-outline btn-confirm-microdeposit"
                          :disabled="isReadOnly"
                          @click="redirectToConfirmMicrodepositPage(funding.id)">Confirm Micro-deposit</button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p class="left-margin top-margin" v-if="fundingSources.length > 1 && showFundingTable"><i>Please note you cannot select an unverified bank account as your default.</i></p>
            </div>

            <!-- Mobile Card View -->
            <div class="mobile-card-view" v-show="showFundingTable">
              <div class="funding-cards-mobile">
                <div 
                  v-for="(funding, index) in fundingSources" 
                  :key="index"
                  class="funding-card-mobile"
                >
                  <div class="funding-card-mobile-header">
                    <div class="funding-card-mobile-icon">
                      <i class="fa fa-university"></i>
                    </div>
                    <div class="funding-card-mobile-title-section">
                      <div class="funding-card-mobile-title">{{funding.bankName ? funding.bankName : '-----'}}</div>
                      <span 
                        v-if="funding.type !== fundingSourceTypes.CHECK.name && funding.type !== fundingSourceTypes.WIRE_TRANSFER.name"
                        class="label funding-card-mobile-status" 
                        v-bind:class="funding.status === 'verified' ? 'label-success' : 'label-warning'">
                        {{funding.status}}
                      </span>
                    </div>
                  </div>
                  
                  <div class="funding-card-mobile-body">
                    <div class="funding-card-mobile-row">
                      <span class="funding-card-mobile-label">Account name:</span>
                      <span class="funding-card-mobile-value">{{funding.name}}</span>
                    </div>
                    
                    <div class="funding-card-mobile-row">
                      <span class="funding-card-mobile-label">Type:</span>
                      <span class="funding-card-mobile-value">{{formatType(funding.type, 'investor')}}</span>
                    </div>

                    <div v-if="funding.status === 'verified'" class="funding-card-mobile-row funding-card-mobile-default">
                      <span class="funding-card-mobile-label">Set as default:</span>
                      <div class="funding-card-mobile-radio">
                        <input 
                          type="radio" 
                          :id="`mobile-funding-${funding.id}`"
                          :value="funding.id"
                          v-model="user.accounts[activeAccountIndex].defaultFundingSourceId"
                          :disabled="isReadOnly"
                          @change="showConfirmationModal({ fundingSource: funding })"
                          class="funding-radio-input"
                        />
                        <label 
                          :for="`mobile-funding-${funding.id}`"
                          :class="{'funding-radio-label': true, 'is-default': user.accounts[activeAccountIndex].defaultFundingSourceId === funding.id}">
                          {{ user.accounts[activeAccountIndex].defaultFundingSourceId === funding.id ? 'Default account' : 'Make default' }}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div class="funding-card-mobile-actions">
                    <button 
                      v-if="funding.status !== 'verified'" 
                      type="button"
                      class="btn btn-mobile-action btn-verify"
                      :disabled="isReadOnly"
                      @click="redirectToConfirmMicrodepositPage(funding.id)">
                      <i class="fa fa-check-circle"></i> Confirm Micro-deposit
                    </button>
                    <button
                      type="button"
                      class="btn btn-mobile-action btn-delete"
                      :disabled="isReadOnly"
                      @click.prevent="removeFundingSource(funding.id)">
                      <i class="fa fa-trash"></i> Delete account
                    </button>
                  </div>
                </div>
              </div>
              <p class="mobile-note" v-if="fundingSources.length > 1"><i>Please note you cannot select an unverified bank account as your default.</i></p>
            </div>

            <div class="row" v-if="fundingSourcesTypeBankLength >= maxAmountFundingSources">
              <div class="col-md-12 left-margin">
                <strong><p class="text-red">The maximum number of online bank accounts is 3.</p></strong>
              </div>
            </div>
            <button
              v-if="user.accounts[activeAccountIndex].accountType!=='INSTITUTION' && !disableInvestment"
              :disabled="isReadOnly || (fundingSourcesTypeBankLength >= maxAmountFundingSources) || isStateRestricted(user.residentialAddress.state)"
              type="button"
              @click="verifyPrimaryInvestor()"
              class="btn btn-default btn-lg x-space addButton top-margin"
            >
                Add a bank account
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import {
    mapActions,
    mapGetters,
    mapMutations,
    mapState,
  } from 'vuex';
  import moment from 'moment';
  import httpService from '@/api/HttpService';

  import {
    SET_USER,
    SHOW_ERRORS_MODAL,
    SET_ERRORS_MODAL_INFO,
    SHOW_QUESTION_MODAL,
    HIDE_QUESTION_MODAL,
    SHOW_INFORMATION_MODAL,
  } from '@/store/mutation-types';

  import Errors from '@/components/modals/Errors';
  import Question from '@/components/modals/Question';
  import Information from '@/components/modals/Information';
  import BankAccountChangedWarning from '@/components/modals/BankAccountChangedWarning';
  import Spinner from '@/components/widgets/Spinner';
  import DismissableAlert from '@/components/widgets/DismissableAlert';
  import OnboardingWizard from '@/components/OnboardingWizard';
  import logActions from '@/data/static/logActions';

  import { formatType, logAction, logglyTags, isStateRestricted } from '@/util';

  import {
    Constants,
  } from '../data/static/constants';

  const mix = {
    methods: {
      formatType,
      isStateRestricted,
    },
  };

  export default {
    name: 'addFundingSource',
    async beforeMount() {
      this.showFundingSourceActionSuccess = this.$route.params.addedFundingSource;
      if (this.showFundingSourceActionSuccess) {
        this.fundingSourceAction = 'added';
      }
      await this.initialize();
    },
    mixins: [mix],
    data() {
      return {
        maxAmountFundingSources: 3,
        fundingSourcesTypeBankLength: 0,
        previousFundingSourceId: null,
        fundingSources: [],
        showFundingTable: true,
        plaidService: window.Plaid,
        loading: false,
        isLegacyUser: false,
        showPendingMicrodepositsLabel: false,
        legacyFundingSourceMessageFirstLine:
          Constants.purchaseNote.legacyFundingSourceMessageFirstLine,
        legacyFundingSourceMessageSecondLine:
          Constants.purchaseNote.legacyFundingSourceMessageSecondLine,
        onBoardingCompleted: false,
        confirmConsentModalShown: false,
        activeAccountIndex: 0,
        showFundingSourceActionSuccess: false,
        fundingSourceAction: '',
        addFundingSourceUsingPlaidSuccess: false,
        isBankAccountChangedWarningVisible: false,
      };
    },
    computed: {
      ...mapState('account', [
        'settingDefaultFundingSource',
      ]),
      ...mapState('home', [
        'userStatus',
      ]),
      ...mapState('app', [
        'user',
        'activeAccountId',
        'activeAccount',
        'fundingSourceTypes',
      ]),
      ...mapState('errors', [
        'isVisibleErrorsModal',
      ]),
      ...mapState('question', [
        'display',
      ]),
      ...mapState('information', [
        'isInformationModalVisible',
      ]),
      ...mapGetters('app', [
        'hasBeenCreatedAccount',
      ]),
      disableInvestment() {
        const { accounts, disableInvestment } = this.user;
        const account = accounts.find(acct => acct.id === this.activeAccount.id);
        return disableInvestment || account.disableInvestment;
      },
      isReadOnly() {
        return this.user && this.user.userType === 'READ_ONLY';
      },
    },
    methods: {
      ...mapActions('home', [
        'getUserStatus',
      ]),
      ...mapActions('accountCreation', [
        'getUserProfile',
        'updateConsentModalFlag',
      ]),
      ...mapActions('account', [
        'setDefaultFundingSource',
        'getAccountFundingSources',
      ]),
      ...mapActions('fundingSource', [
        'createDwollaUser',
        'updateDwollaCustomer',
      ]),
      ...mapActions('fundingSource', [
        'deleteFundingSource',
      ]),
      ...mapMutations('app', {
        setUser: SET_USER,
      }),
      ...mapMutations('question', {
        hideQuestionModal: HIDE_QUESTION_MODAL,
        showQuestionModal: SHOW_QUESTION_MODAL,
      }),
      ...mapMutations('errors', {
        showErrorsModal: SHOW_ERRORS_MODAL,
        setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
      }),
      ...mapMutations('information', {
        showInformationModal: SHOW_INFORMATION_MODAL,
      }),
      async initialize() {
        await this.loadUserProfile();
        const response = await this.checkUserStatus();
        if (response && (response.status === 'document' || response.status === 'kba')) return;
        await this.loadFundingSources();
      },
      async loadUserProfile() {
        this.loading = true;

        const data = await this.getUserProfile();
        this.setUser(data);

        data.accounts.forEach((account, idx) => {
          if (account.accountType === 'INDIVIDUAL') {
            this.onBoardingCompleted = !!account.onBoardingCompleted;
            this.confirmConsentModalShown = !!account.confirmConsentModalShown;
            this.previousFundingSourceId = account.defaultFundingSourceId;
          }

          if (account.id === this.activeAccountId) {
            this.activeAccountIndex = idx;
          }
        });

        this.loading = false;
      },
      async loadFundingSources() {
        this.loading = true;
        this.showFundingTable = true;
        const data = await this.getAccountFundingSources(this.activeAccountId);

        this.fundingSources = data.filter(fss =>
          (fss.type === 'bank' || fss.type === 'check' || fss.type === 'wire/ach')
            && !fss.removed);

        this.verifyFundingSources(this.fundingSources);
        if (this.fundingSources.length === 0) {
          if (this.onBoardingCompleted) {
            this.showFundingTable = true;
          } else {
            this.addAccount();
          }
        }
        this.fundingSourcesTypeBankLength = this.fundingSources.filter(fs => fs.type === 'bank').length;
        this.loading = false;
        return this.fundingSources;
      },
      legacyFundingSourcesExist() {
        const containsLegacyFundingSource = this.fundingSources.find(
          fs => (fs.type === 'check' || fs.type === 'wire/ach') && !fs.removed);

        return containsLegacyFundingSource !== undefined;
      },
      methodToExecuteInQuestionModal(fundingSourceId) {
        this.hideQuestionModal();
        const account = this.user.accounts[this.activeAccountIndex];
        this.setDefaultFundingSource({
          accountId: account.id,
          fundingSourceId,
        }).then(() => {
          this.previousFundingSourceId = fundingSourceId;
          this.user.accounts[this.activeAccountIndex].defaultFundingSourceId = fundingSourceId;
          this.showBankAccountChangedWarning();
        });
      },
      redirectToConfirmMicrodepositPage(fundingSourceId) {
        if (this.isReadOnly) return;
        this.$router.push({
          name: 'MicroDepositsVerification',
          params: { id: fundingSourceId },
        });
      },
      showConfirmationModal({
        fundingSource,
      }) {
        if (this.isReadOnly) return;
        this.user.accounts[this.activeAccountIndex].defaultFundingSourceId
          = this.previousFundingSourceId;
        this.showQuestionModal({
          title: Constants.fundingSource.defaultFundingSourceModalTitle,
          question: `Are you sure you want to select ${fundingSource.name} as your new default bank account?`,
          method: () => {
            this.methodToExecuteInQuestionModal(fundingSource.id);
          },
          yesButtonText: Constants.purchaseNote.modalYesButtonText,
          hideCancelButton: false,
        });
      },
      showDeleteConfirmationModal(fundingSourceId) {
        if (this.isReadOnly) return;
        this.showQuestionModal({
          title: Constants.fundingSource.deleteFundingSourceModalTitle,
          question: 'Are you sure you want to delete this bank account?',
          method: () => this.deleteFundingSource(fundingSourceId)
            .then(() => {
              this.hideQuestionModal();
              const indexToRemove = this.fundingSources.findIndex(fs => fs.id === fundingSourceId);
              if (indexToRemove > -1) {
                this.fundingSources.splice(indexToRemove, 1);
              }
              this.showFundingSourceActionSuccess = true;
              this.fundingSourceAction = 'deleted';
              this.verifyFundingSources(this.fundingSources);
              this.loadFundingSources();
            }).catch((err) => {
              this.loading = false;
              this.hideQuestionModal();
              let errorMessage = '';
              if (err.message === 'PENDING_TRANSACTIONS') {
                errorMessage = `You may have a transaction in process or an upcoming payment for this bank account.
                You won't be able to delete it until those transactions have been finalized or you have changed the upcoming payments to a different bank account.
                 If you believe that transactions in progress will not complete due to a block or other issue on your bank account,
                 please contact the Calvert Impact Capital and we will assist you.`;
              }
              if (err.message === 'DEFAULT_FUNDING_SOURCE') {
                errorMessage = 'Please make sure you\'ve selected another bank account as your default one before attempting to delete this one.';
              }
              if (err.message === 'NOT_PRIMARY_INVESTOR') {
                errorMessage = 'Only primary investors can remove bank accounts, please ask the primary investor on this account to remove this bank account.';
              }
              this.showErrorModal(errorMessage || err);
            }),
          yesButtonText: Constants.purchaseNote.modalYesButtonText,
          hideCancelButton: false,
        });
      },
      hideFundingSourceActionSuccess() {
        this.showFundingSourceActionSuccess = false;
      },
      showErrorModal(err) {
        const listErrors = [];
        listErrors.push(err);

        this.setErrorsModalInfo(listErrors);
        this.showErrorsModal();
      },
      verifyFundingSources(fundingSources) {
        const matchedFundingSource = fundingSources.find(fundingSource =>
          typeof fundingSource._links !== 'undefined' && // eslint-disable-line no-underscore-dangle
          !!fundingSource._links['verify-micro-deposits'], // eslint-disable-line no-underscore-dangle
        );

        this.showPendingMicrodepositsLabel = !!(matchedFundingSource && matchedFundingSource.id);
      },
      selectDefaultFundingSource(fundingSources, fundingSourceId) {
        if (this.previousFundingSourceId === null) {
          const fundingSource = fundingSources.find(fs => fs.id === fundingSourceId);
          if (fundingSource.status === 'verified') {
            const account = this.user.accounts[this.activeAccountIndex];
            this.setDefaultFundingSource({ accountId: account.id, fundingSourceId });
            this.user.accounts[this.activeAccountIndex].defaultFundingSourceId = fundingSourceId;
            this.previousFundingSourceId = fundingSourceId;
            this.showBankAccountChangedWarning();
          }
        }
      },
      async addAccount() {
        this.showFundingTable = false;
        this.showPendingMicrodepositsLabel = false;
        this.hideFundingSourceActionSuccess();
        this.addFundingSourceUsingPlaid();
      },
      addedBankAccountSuccess() {
        this.showFundingSourceActionSuccess = true;
        this.fundingSourceAction = 'added';
      },
      retryModal() {
        this.showQuestionModal({
          title: Constants.fundingSource.modalTitle,
          question: Constants.fundingSource.retryDisclamer,
          questionAppend: Constants.fundingSource.legacyDisclamerRetry,
          yesButtonText: Constants.fundingSource.modalEditProfileButtonText,
          method: async () => {
            this.hideQuestionModal();
            this.loading = true;
            this.$router.push({ name: 'EditUserProfile' });
          } });
      },
      verifyPrimaryInvestor() {
        if (this.user.id !== this.activeAccount.primaryInvestorId) {
          const restrictedMessage = 'If you would like to purchase Notes online and/or have repayments go directly to a bank account, please ask the primary investor on this account to add one or more bank accounts.';
          this.showInformationModal(restrictedMessage);
        } else {
          this.addBankAccount();
        }
      },
      async addFundingSourceUsingPlaid() {
        this.success = false;
        const activeAccount = this.user.accounts && this.user.accounts[this.activeAccountIndex];
        const dwollaCustomerId =
          (activeAccount && activeAccount.dwollaCustomerId) || this.user.dwollaCustomerId;
        const linkHandler = this.plaidService.create({
          token: (await httpService.postV2('/funding-sources/link-token', {
            dwollaCustomerId,
          })).link_token,
          onLoad() {
            // The Link module finished loading.
          },
          onSuccess: async (publicToken, metadata) => {
            this.loading = true;
            this.addFundingSourceUsingPlaidSuccess = true;
            this.addedBankAccountSuccess();
            try {
              const response = await httpService.postV2('/funding-sources/exchange-link-token', {
                publicToken,
                accounts: metadata.accounts,
                dwollaCustomerId,
              });

              const { accountName, accountId } = this.user.accounts[this.activeAccountIndex];
              logAction({
                user: this.user.userId,
                level: 'info',
                action: logActions.ADD_FUNDING_SOURCE.name,
                message: `New bank account added to account ${accountId} ${accountName}`,
                tags: [logglyTags.dwolla],
              });

              const [account] = metadata.accounts;
              const selectedAccount = response.find(r => r.id === account.id);
              const fundingSources = await this.loadFundingSources();

              await this.loadUserProfile();

              if (selectedAccount) {
                const fundingSourceIdAdded = selectedAccount.location.split('/').pop();
                if (fundingSourceIdAdded) {
                  this.selectDefaultFundingSource(fundingSources, fundingSourceIdAdded);
                }
              }

              await this.checkUserStatus();
            } catch (error) {
              let errorMessage = 'Something went wrong please try again!';

              if (error.message === 'DuplicateResource') {
                errorMessage = 'You have already added the account.';
              } else if (error.message === 'DATABASE_AUTH_FAIL') {
                errorMessage = "We couldn't verify this account with your bank. Please try a different account or use micro-deposit verification instead.";
              }
              this.showErrorModal(errorMessage);
            } finally {
              this.loading = false;
              this.showFundingTable = true;
            }
          },
          onExit: (err, metadata) => {
            if (!this.addFundingSourceUsingPlaidSuccess) {
              this.showFundingTable = true;
            }
            // The user exited the Link flow.
            if (err != null) {
              // The user encountered a Plaid API error
              // prior to exiting.
            }
            if (metadata) {
              // metadata contains information about the institution
              // that the user selected and the most recent
              // API request IDs.
              // Storing this information can be helpful for support.
            }
          },
        });
        linkHandler.open();
      },
      async addBankAccount() {
        if (this.isReadOnly) return;
        if (!this.user.socialSecurityNum || !moment(this.user.dateOfBirth, 'YYYY-MM-DD', true).isValid()) {
          this.retryModal();
          return;
        }
        const isLegacyUser = await this.showLegacyDisclamer();
        try {
          if (isLegacyUser && !this.confirmConsentModalShown) {
            await this.showDisclaimerConfirmationModal();
          } else if (isLegacyUser && this.confirmConsentModalShown) {
            this.loading = true;
            await this.createDwollaUser(this.user.id);
            await this.initialize();
          }
        } catch (error) {
          this.loading = false;
          if (error.statusCode === 400) {
            this.retryModal();
            return;
          }
        }
        this.loading = true;
        if (this.userStatus && this.userStatus.status === 'retry') {
          await this.updateDwollaCustomer();
        }
        this.getUserStatus({
          userId: this.user.id,
          accountId: this.activeAccount.id,
          userFirstName: this.user.firstName,
          userLastName: this.user.lastName,
        }).then(({ status }) => {
          this.loading = false;
          if (status === 'retry') {
            this.retryModal();
          } else if (status === 'document_migrated_user') {
            this.$router.push({ name: 'DocumentUpload' });
          } else {
            this.addAccount();
          }
        });
      },
      showLegacyDisclamer() {
        return this.loadUserProfile()
        .then(() => {
          const individualAccount = this.user.accounts.find(ac => ac.accountType === 'INDIVIDUAL');
          this.isLegacyUser = individualAccount.dwollaCustomerId === null;
          return this.isLegacyUser;
        });
      },
      showDisclaimerConfirmationModal() {
        return new Promise((resolve) => {
          this.showQuestionModal({
            title: Constants.fundingSource.modalTitle,
            question: Constants.fundingSource.legacyDisclamer,
            hyperLinkUrl: Constants.fundingSource.hyperLinkUrl,
            hyperLinkText: Constants.fundingSource.hyperLinkText,
            hyperLinkUrl2: Constants.fundingSource.hyperLinkUrl2,
            hyperLinkText2: Constants.fundingSource.hyperLinkText2,
            questionAppend: Constants.fundingSource.legacyDisclamerAppend,
            method: async () => {
              try {
                this.hideQuestionModal();
                this.loading = true;
                this.updateConsentModalFlag();
                await this.createDwollaUser(this.user.id);
                await this.initialize();
                resolve(true);
              } catch (error) {
                this.loading = false;
                if (error.statusCode === 400) {
                  this.retryModal();
                }
              }
            },
            yesButtonText: Constants.fundingSource.modalYesButtonText,
            hideCancelButton: false,
          });
        });
      },
      async removeFundingSource(fundingSourceId) {
        this.showDeleteConfirmationModal(fundingSourceId);
      },
      async checkUserStatus() {
        try {
          this.loading = true;

          if (!this.hasBeenCreatedAccount) {
            this.$router.push({ name: 'AccountSummary' });
          }

          const response = await this.getUserStatus({
            accountId: this.activeAccount.id,
          });

          const { needs, status } = response;

          if (status === 'kba') {
            this.$router.push({ name: 'KbaVerification' });
          } else if (status === 'document') {
            this.$router.push({ name: 'DocumentUpload' });
          } else if (status === 'retry') {
            this.$router.push({ name: 'AccountCreation' });
          } else if (status === 'suspended') {
            this.$router.push({ name: 'AccountSuspended' });
          } else if (!needs.includes('FUNDING_SOURCE') && needs.includes('NOTE')) {
            this.$router.push({ name: 'PurchaseNote', query: { first: 1 } });
          } else {
            this.loading = false;
          }
          return response;
        } catch (error) {
          this.showErrorModal(error.message);
          this.loading = false;
          return error;
        }
      },
      showBankAccountChangedWarning() {
        this.isBankAccountChangedWarningVisible = true;
      },
      hideBankAccountChangedWarning() {
        this.isBankAccountChangedWarningVisible = false;
      },
      handleGoToUpcomingTransactions() {
        this.$router.push({ name: 'UpcomingTransactions' });
      },
    },
    components: {
      errors: Errors,
      question: Question,
      BankAccountChangedWarning,
      spinner: Spinner,
      information: Information,
      'onboarding-wizard': OnboardingWizard,
      'dismissable-alert': DismissableAlert,
    },
  };
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .addButton {
    font-size: 14px;
    display: block;
    margin: 16px 0;
    background-color: #127285;
  }
  .top-margin {
    margin-top: 3%;
  }
  .left-margin {
    margin-left: 0%;
  }
  .table-fundings {
    width: 100%;
    margin-left: auto;
    margin-right: auto;
  }
  .panel-height {
    min-height: 400px;
    height: auto;
  }
  .text-red {
    color: #a94442;
  }
  .btn-confirm-microdeposit {
    margin-left: 20px;
  }
  input[type="radio"] {
    margin: auto;
  }

  /* Mobile card view - hidden on desktop */
  .mobile-card-view {
    display: none;
  }

  /* Desktop table view - visible by default */
  .desktop-table {
    display: block;
  }

  /* Mobile responsive styles */
  @media (max-width: 1024px) {
    /* Hide desktop table, show mobile cards */
    .desktop-table {
      display: none !important;
    }

    .mobile-card-view {
      display: block;
      padding: 0 20px;
    }

    .content-heading {
      font-size: 22px !important;
      padding: 20px 20px 16px 20px !important;
    }

    .panel-default {
      padding: 20px 0 !important;
      margin: 0 !important;
    }

    .panel-title {
      font-size: 20px !important;
      padding: 0 20px;
      margin-bottom: 12px !important;
    }

    .accounts-title {
      margin: 16px 0 !important;
    }

    /* Alert responsive */
    .alert {
      margin: 16px 20px !important;
      font-size: 16px !important;
      padding: 16px !important;
    }

    .alert h4 {
      font-size: 17px !important;
      margin-bottom: 8px;
    }

    .alert p {
      font-size: 15px !important;
      margin-bottom: 0;
    }

    /* Button responsive */
    .addButton {
      width: 100% !important;
      font-size: 17px !important;
      padding: 14px 20px !important;
      margin: 16px 20px !important;
      max-width: calc(100% - 40px);
    }

    /* Card styles */
    .funding-cards-mobile {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-top: 16px;
    }

    .funding-card-mobile {
      background-color: #fff;
      border: 1px solid #e8e8e8;
      border-radius: 8px;
      padding: 0;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    }

    .funding-card-mobile-header {
      display: flex;
      align-items: center;
      padding: 18px 20px;
      background-color: #fafbfc;
      border-bottom: 1px solid #e8e8e8;
    }

    .funding-card-mobile-icon {
      flex-shrink: 0;
      margin-right: 14px;
      font-size: 26px;
      color: #00788d;
    }

    .funding-card-mobile-title-section {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .funding-card-mobile-title {
      font-size: 18px;
      font-weight: 600;
      color: #333;
      word-break: break-word;
    }

    .funding-card-mobile-status {
      align-self: flex-start;
      font-size: 13px;
      padding: 4px 10px;
    }

    .funding-card-mobile-body {
      padding: 18px 20px;
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .funding-card-mobile-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
    }

    .funding-card-mobile-default {
      padding-top: 12px;
      margin-top: 6px;
      border-top: 1px solid #f0f0f0;
    }

    .funding-card-mobile-label {
      font-size: 15px;
      color: #888;
      flex-shrink: 0;
      min-width: 110px;
    }

    .funding-card-mobile-value {
      font-size: 16px;
      color: #333;
      font-weight: 500;
      text-align: right;
      word-break: break-word;
    }

    .funding-card-mobile-radio {
      display: flex;
      align-items: center;
    }

    .funding-radio-input {
      width: 22px;
      height: 22px;
      margin: 0 10px 0 0;
      cursor: pointer;
    }

    .funding-radio-label {
      font-size: 15px;
      color: #127285;
      font-weight: 500;
      cursor: pointer;
      margin: 0;
    }

    .funding-radio-label.is-default {
      color: #558c1d;
      font-weight: 600;
    }

    .funding-card-mobile-actions {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 16px 20px;
      background-color: #fafbfc;
      border-top: 1px solid #e8e8e8;
    }

    .btn-mobile-action {
      width: 100%;
      font-size: 16px;
      padding: 12px 16px;
      border-radius: 6px;
      font-weight: 500;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      transition: all 0.2s;
    }

    .btn-mobile-action i {
      font-size: 16px;
    }

    .btn-verify {
      background-color: #127285;
      color: #fff;
      border: none;
    }

    .btn-verify:hover:not(:disabled) {
      background-color: #0d5a68;
    }

    .btn-delete {
      background-color: transparent;
      color: #d9534f;
      border: 1px solid #d9534f;
    }

    .btn-delete:hover:not(:disabled) {
      background-color: #d9534f;
      color: #fff;
    }

    .btn-mobile-action:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .mobile-note {
      margin: 16px 0 0 0;
      font-size: 15px;
      color: #666;
      font-style: italic;
    }

    /* Additional bank account button */
    .x-space {
      margin-left: 20px !important;
      margin-right: 20px !important;
    }

    .text-red {
      font-size: 16px;
    }
  }

  @media (max-width: 480px) {
    .mobile-card-view {
      padding: 0 16px;
    }

    .content-heading {
      font-size: 20px !important;
      padding: 18px 16px 14px 16px !important;
    }

    .panel-title {
      font-size: 18px !important;
      padding: 0 16px;
    }

    .alert {
      margin: 14px 16px !important;
      font-size: 15px !important;
      padding: 14px !important;
    }

    .alert h4 {
      font-size: 16px !important;
    }

    .alert p {
      font-size: 14px !important;
    }

    .addButton {
      font-size: 16px !important;
      padding: 13px 18px !important;
      margin: 14px 16px !important;
      max-width: calc(100% - 32px);
    }

    .funding-card-mobile-header {
      padding: 16px 16px;
    }

    .funding-card-mobile-icon {
      font-size: 24px;
      margin-right: 12px;
    }

    .funding-card-mobile-title {
      font-size: 17px;
    }

    .funding-card-mobile-status {
      font-size: 12px;
      padding: 3px 8px;
    }

    .funding-card-mobile-body {
      padding: 16px 16px;
    }

    .funding-card-mobile-label {
      font-size: 14px;
      min-width: 100px;
    }

    .funding-card-mobile-value {
      font-size: 15px;
    }

    .funding-card-mobile-actions {
      padding: 14px 16px;
    }

    .btn-mobile-action {
      font-size: 15px;
      padding: 11px 14px;
    }

    .btn-mobile-action i {
      font-size: 15px;
    }

    .funding-radio-input {
      width: 20px;
      height: 20px;
    }

    .funding-radio-label {
      font-size: 14px;
    }

    .mobile-note {
      font-size: 14px;
    }

    .x-space {
      margin-left: 16px !important;
      margin-right: 16px !important;
    }
  }

  @media (max-width: 360px) {
    .mobile-card-view {
      padding: 0 14px;
    }

    .content-heading {
      font-size: 18px !important;
      padding: 16px 14px 12px 14px !important;
    }

    .panel-title {
      font-size: 17px !important;
      padding: 0 14px;
    }

    .alert {
      margin: 12px 14px !important;
      font-size: 14px !important;
      padding: 12px !important;
    }

    .alert h4 {
      font-size: 15px !important;
    }

    .alert p {
      font-size: 13px !important;
    }

    .addButton {
      font-size: 15px !important;
      padding: 12px 16px !important;
      margin: 12px 14px !important;
      max-width: calc(100% - 28px);
    }

    .funding-card-mobile-header {
      padding: 14px 14px;
    }

    .funding-card-mobile-icon {
      font-size: 22px;
      margin-right: 10px;
    }

    .funding-card-mobile-title {
      font-size: 16px;
    }

    .funding-card-mobile-body {
      padding: 14px 14px;
    }

    .funding-card-mobile-label {
      font-size: 13px;
      min-width: 90px;
    }

    .funding-card-mobile-value {
      font-size: 14px;
    }

    .funding-card-mobile-actions {
      padding: 12px 14px;
    }

    .btn-mobile-action {
      font-size: 14px;
      padding: 10px 12px;
    }

    .btn-mobile-action i {
      font-size: 14px;
    }

    .funding-radio-input {
      width: 18px;
      height: 18px;
    }

    .funding-radio-label {
      font-size: 13px;
    }

    .mobile-note {
      font-size: 13px;
    }

    .x-space {
      margin-left: 14px !important;
      margin-right: 14px !important;
    }
  }
</style>



// WEBPACK FOOTER //
// AddFundingSource.vue?2dfd25a4